import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Crown, LogOut, UserX, ArrowRightLeft, Loader2, Users } from "lucide-react";
import { useGroups } from "@/context/GroupContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import api from "@/lib/api";

export default function GroupMembersDialog({ open, onOpenChange }) {
  const { activeGroup, refresh, leaveGroup } = useGroups();
  const { user } = useAuth();
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: 'remove'|'transfer'|'leave', member?, label }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeGroup || !open) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${activeGroup.id}/members`);
      setMembers(data);
    } catch (e) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [activeGroup, open]);

  useEffect(() => { load(); }, [load]);

  if (!activeGroup) return null;
  const isOwner = activeGroup.is_owner;

  const doAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.type === "remove") {
        await api.delete(`/groups/${activeGroup.id}/members/${confirm.member.id}`);
        toast.success(`${confirm.member.name || confirm.member.email} removed`);
      } else if (confirm.type === "transfer") {
        await api.post(`/groups/${activeGroup.id}/transfer/${confirm.member.id}`);
        toast.success(`Ownership transferred to ${confirm.member.name || confirm.member.email}`);
        await refresh();
      } else if (confirm.type === "leave") {
        const ok = await leaveGroup(activeGroup.id);
        if (ok) {
          toast.success("You left the group");
          onOpenChange(false);
          setConfirm(null);
          setBusy(false);
          return;
        }
      }
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="group-members-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Users className="w-5 h-5 text-olive" /> {t("members.title")}
            </DialogTitle>
            <DialogDescription>
              {isOwner ? t("members.sub_owner") : t("members.sub_member")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2 max-h-[55vh] overflow-y-auto scrollbar-thin">
            {loading && (
              <div className="grid place-items-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-espresso/60" />
              </div>
            )}
            {!loading && members.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 bg-cream rounded-lg px-3 py-2.5 border border-app-border"
                data-testid={`member-row-${m.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-olive/15 text-olive grid place-items-center font-bold uppercase">
                  {(m.name || m.email)[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-espresso truncate">{m.name || m.email}</span>
                    {m.is_owner && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                        <Crown className="w-2.5 h-2.5" /> {t("members.owner")}
                      </span>
                    )}
                    {m.id === user?.id && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-espresso/50">
                        {t("common.you")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-espresso/60 truncate">{m.email}</div>
                </div>

                {isOwner && !m.is_owner && (
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirm({ type: "transfer", member: m })}
                      data-testid={`transfer-${m.id}`}
                      title="Transfer ownership"
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirm({ type: "remove", member: m })}
                      className="text-danger hover:text-danger"
                      data-testid={`remove-${m.id}`}
                      title="Remove from group"
                    >
                      <UserX className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {!m.is_owner && m.id === user?.id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirm({ type: "leave" })}
                    className="text-danger hover:text-danger"
                    data-testid="leave-group-btn"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-1" /> {t("members.leave")}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="bg-cream-2 rounded-lg p-3 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-olive font-bold mb-1">{t("members.invite_more")}</div>
            <div className="font-mono tracking-widest text-espresso font-bold text-sm">
              {activeGroup.invite_code}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "remove" && t("members.remove_title")}
              {confirm?.type === "transfer" && t("members.transfer_title")}
              {confirm?.type === "leave" && t("members.leave_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "remove" &&
                `${confirm.member?.name || confirm.member?.email} will lose access to "${activeGroup.name}".`}
              {confirm?.type === "transfer" &&
                `${confirm.member?.name || confirm.member?.email} will become the new owner. You will stay as a regular member.`}
              {confirm?.type === "leave" &&
                `You will lose access to "${activeGroup.name}". You can rejoin later with the invite code.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doAction}
              disabled={busy}
              className={confirm?.type === "transfer" ? "bg-olive hover:bg-olive-hover" : "bg-danger hover:bg-danger/90"}
              data-testid="confirm-action"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                confirm?.type === "remove" ? t("members.remove") :
                confirm?.type === "transfer" ? t("members.transfer") : t("members.leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
